import { formatInteger, type InvestmentResult } from '@brimo/shared';
import { clsx } from '../../../../ds/clsx';
import type { CalculatorContext, InvestmentInputValues } from '../../types';
import { depreciationTotal, eur, pct, rentIncreaseLine } from '../model';
import { Badge, DlRow, Figure, Section } from './parts';
import styles from '../advisor.module.css';

const SHARE_SCALE_MAX = 120;

export interface FactsSectionsProps {
  context: CalculatorContext;
  values: InvestmentInputValues;
  result: InvestmentResult;
  purchaseYear: number;
}

function perSqm(cents: number, area: number): string | null {
  return area > 0 ? `${eur(cents / area)}/m²` : null;
}

/** Sections 3 „Ihr Kauf", 4 „Ihre Finanzierung" and 5 „Miete und Kosten | Steuer". */
export function FactsSections({ context, values, result, purchaseYear }: FactsSectionsProps) {
  const area = values.areaSqm;
  const parkingCents = Math.min(context.unit.parkingSpacePriceCents ?? 0, values.purchasePriceCents);
  const unitPriceCents = values.purchasePriceCents - parkingCents;
  const grossPriceCents = values.purchasePriceCents + result.acquisitionCostsCents;
  const renovationNetCents = Math.max(0, values.renovationCents - values.renovationSubsidyCents);

  const loan = result.loanCents;
  const share = values.purchasePriceCents > 0 ? (loan / values.purchasePriceCents) * 100 : 0;
  const fill = Math.min(share, SHARE_SCALE_MAX) / SHARE_SCALE_MAX;
  const fixedEnd = purchaseYear + values.fixedInterestYears - 1;

  const year1 = result.years[0];
  const rentMonthly = (year1?.rentCents ?? values.rentCents * 12) / 12;
  const costsMonthly = values.deductibleCostsCents + values.nonDeductibleCostsCents;
  const linear = values.depreciationManualCents === null;
  const usefulLife =
    linear && values.depreciationPct > 0
      ? new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(100 / values.depreciationPct)
      : null;
  const depreciationYear = year1?.depreciationCents ?? result.depreciationCents;
  const additionalYear = year1?.additionalDepreciationCents ?? 0;
  const total = depreciationTotal(values, result);
  const baseShare = grossPriceCents > 0 ? result.depreciationBaseCents / grossPriceCents : 0;

  return (
    <>
      <Section id="advisor-purchase" title="Ihr Kauf">
        <div className={styles.card}>
          <div className={styles.dlGrid}>
            <dl className={styles.dl}>
              <DlRow term="Kaufpreis" value={eur(unitPriceCents)} hint={perSqm(unitPriceCents, area)} />
              <DlRow term="Stellplatz" value={eur(parkingCents)} />
              <DlRow
                term="Kaufnebenkosten"
                value={eur(result.acquisitionCostsCents)}
                hint={pct(values.acquisitionCostsPct, 2)}
              />
              <DlRow
                term="Gesamtkaufpreis"
                value={eur(grossPriceCents)}
                hint={perSqm(grossPriceCents, area)}
                total
              />
            </dl>
            <dl className={styles.dl}>
              <DlRow term="Renovierungskosten" value={eur(values.renovationCents)} />
              <DlRow
                term="Renovierungszuschuss"
                value={values.renovationSubsidyCents > 0 ? `−${eur(values.renovationSubsidyCents)}` : eur(0)}
              />
              <DlRow term="Mietsubvention" value={eur(values.rentSubsidyCents)} />
              <DlRow term="Ihr Eigenkapital" value={eur(result.equityCents)} />
              <DlRow
                term="Gesamtinvestition"
                value={
                  <>
                    {area > 0 ? (
                      <Badge tone="green">{eur(result.totalInvestmentCents / area)}/m²</Badge>
                    ) : null}
                    {eur(result.totalInvestmentCents)}
                  </>
                }
                total
              />
            </dl>
          </div>
          {renovationNetCents === 0 && values.renovationCents > 0 ? (
            <p className={clsx(styles.note, styles.extraLine)}>
              Der Renovierungszuschuss deckt die Renovierungskosten vollständig.
            </p>
          ) : null}
        </div>
      </Section>

      <Section id="advisor-financing" title="Ihre Finanzierung">
        <div className={styles.card}>
          <div className={styles.finTop}>
            <Figure label="Darlehen" value={eur(loan)} />
            <div className={styles.finRight}>
              <Figure label="Anteil am Kaufpreis" value={pct(share, 0)} />
            </div>
          </div>
          <div
            className={styles.shareBar}
            role="img"
            aria-label={`Darlehen: ${pct(share, 0)} des Kaufpreises`}
          >
            <span className={styles.shareFill} style={{ width: `${fill * 100}%` }} />
            <span className={styles.shareTick} style={{ left: `${(100 / SHARE_SCALE_MAX) * 100}%` }} />
          </div>
          <div className={styles.shareScale} aria-hidden="true">
            <span style={{ left: '0%' }}>0 %</span>
            <span style={{ left: `${(100 / SHARE_SCALE_MAX) * 100}%` }}>100 %</span>
            <span style={{ left: '100%' }}>{SHARE_SCALE_MAX} %</span>
          </div>
          <div className={styles.four}>
            <Figure
              label="Sollzins"
              value={pct(values.interestPct, 2)}
              caption={`${eur((loan * values.interestPct) / 100 / 12)} im Monat`}
              small
            />
            <Figure
              label="Tilgung"
              value={pct(values.repaymentPct, 2)}
              caption={`${eur((loan * values.repaymentPct) / 100 / 12)} im Monat`}
              small
            />
            <Figure
              label="Zinsbindung"
              value={`${formatInteger(values.fixedInterestYears)} Jahre`}
              caption={`bis Ende ${fixedEnd}`}
              small
            />
            <Figure
              label="Danach angenommen"
              value={`${pct(values.followUpInterestPct, 2)} / ${pct(values.followUpRepaymentPct, 2)}`}
              caption="Zins / Tilgung"
              small
            />
          </div>
          <div className={styles.sandPanel}>
            <Figure label="Rate an die Bank im Monat" value={eur(result.annuityMonthlyCents)} small />
            <Figure label="Ihr Eigenkapital" value={eur(result.equityCents)} small />
          </div>
        </div>
      </Section>

      <Section id="advisor-rent-tax" title="Miete und Kosten | Steuer">
        <div className={styles.twoCols}>
          <div className={styles.colStack}>
            <h3 className={styles.subHeading}>Miete und Kosten</h3>
            <div className={styles.pair}>
              <span className={styles.label}>Kaltmiete im Monat</span>
              <div className={styles.valueRow}>
                <span className={styles.value}>{eur(rentMonthly)}</span>
                {area > 0 ? <Badge tone="dark">{eur(rentMonthly / area)}/m²</Badge> : null}
              </div>
              <span className={styles.caption}>{rentIncreaseLine(values)}</span>
            </div>
            <Figure
              label="Kosten im Monat"
              value={eur(costsMonthly)}
              caption={
                rentMonthly > 0 ? `${pct((costsMonthly / rentMonthly) * 100)} der Kaltmiete` : undefined
              }
            />
          </div>
          <div className={styles.colStack}>
            <h3 className={styles.subHeading}>Steuer</h3>
            <Figure label="Ihr Steuersatz" value={pct(values.taxRatePct, 0)} />
            {linear ? (
              <Figure
                label="Abschreibung (AfA)"
                value={pct(values.depreciationPct, 2)}
                caption={usefulLife === null ? 'pro Jahr' : `pro Jahr, ${usefulLife} Jahre lang`}
              />
            ) : (
              <Figure
                label="Abschreibung (AfA)"
                value={eur(values.depreciationManualCents ?? 0)}
                caption="fester Betrag pro Jahr"
              />
            )}
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.depreciationTop}>
            <Figure label="Gebäudewert, den Sie abschreiben" value={eur(result.depreciationBaseCents)} />
            <div>
              <Figure label="Anteil am Gesamtkaufpreis" value={pct(baseShare * 100, 0)} small />
              <div
                className={styles.meter}
                role="img"
                aria-label={`Gebäudewert: ${pct(baseShare * 100, 0)} des Gesamtkaufpreises`}
              >
                <div className={styles.meterFill} style={{ width: `${Math.min(1, baseShare) * 100}%` }} />
              </div>
            </div>
          </div>
          <div className={styles.three}>
            <Figure label="Abschreibung im Monat" value={eur(depreciationYear / 12)} small />
            <Figure label="Abschreibung im Jahr" value={eur(depreciationYear)} small />
            <Figure label="Abschreibung gesamt" value={eur(total.cents)} caption={total.caption} small />
          </div>
          {additionalYear > 0 ? (
            <p className={clsx(styles.note, styles.extraLine)}>
              Zusätzlich {eur(additionalYear)} Zusatz-Abschreibung im Jahr, in den ersten 10 Jahren.
            </p>
          ) : null}
          {values.maintenanceExpenseCents > 0 ? (
            <p className={clsx(styles.note, styles.extraLine)}>
              Einmalige Steuererstattung für den Erhaltungsaufwand von {eur(values.maintenanceExpenseCents)}:{' '}
              {eur(result.oneOffTaxRefundCents)} im ersten Jahr.
            </p>
          ) : null}
        </div>
      </Section>
    </>
  );
}
